import { DEFAULT_OP_PARAMS, type OpKind } from "./ops.ts";
import { SCHEMA_VERSION, type Project } from "./project.ts";

export type ParseResult =
  | { readonly ok: true; readonly project: Project; readonly warnings: readonly string[] }
  | { readonly ok: false; readonly error: string };

const KNOWN_OP_KINDS = new Set<string>(Object.keys(DEFAULT_OP_PARAMS) as OpKind[]);
const MEDIA_KINDS = new Set(["image", "audio", "video"]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Guards the boundary between IndexedDB and the editor. A document written by a newer
 * build may carry ops this build cannot run; those are dropped and reported rather than
 * failing the whole load, so an older tab still opens the project read-mostly.
 */
export function parseProject(value: unknown): ParseResult {
  if (!isRecord(value)) return { ok: false, error: "プロジェクトの形式が不正です" };
  if (value["schemaVersion"] !== SCHEMA_VERSION) {
    return { ok: false, error: `未対応のスキーマ version: ${String(value["schemaVersion"])}` };
  }
  if (typeof value["id"] !== "string" || value["id"].length === 0) {
    return { ok: false, error: "プロジェクト id がありません" };
  }
  if (typeof value["kind"] !== "string" || !MEDIA_KINDS.has(value["kind"])) {
    return { ok: false, error: `未対応の種別: ${String(value["kind"])}` };
  }
  if (!Array.isArray(value["sources"]) || !Array.isArray(value["tracks"])) {
    return { ok: false, error: "sources / tracks がありません" };
  }

  const warnings: string[] = [];
  const tracks = value["tracks"].map((track) => {
    if (!isRecord(track) || !Array.isArray(track["clips"])) return track;
    const clips = track["clips"].map((clip) => {
      if (!isRecord(clip) || !Array.isArray(clip["ops"])) return clip;
      const ops = clip["ops"].filter((op) => {
        const known = isRecord(op) && typeof op["t"] === "string" && KNOWN_OP_KINDS.has(op["t"]);
        if (!known) warnings.push(`未対応の操作を読み飛ばしました: ${describeOp(op)}`);
        return known;
      });
      return { ...clip, ops };
    });
    return { ...track, clips };
  });

  return { ok: true, project: { ...value, tracks } as unknown as Project, warnings };
}

function describeOp(op: unknown): string {
  return isRecord(op) && typeof op["t"] === "string" ? op["t"] : "不明";
}
